import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, User, Menu, X, ChevronDown, BarChart2 } from "lucide-react";
import { useApp } from "../context/AppContext";

export default function Header() {
  const { t, tr, settings, categories, wishlist, compare, user, lang, setLang } = useApp();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [openCat, setOpenCat] = useState(null);
  const navigate = useNavigate();

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) {
      navigate(`/shop?q=${encodeURIComponent(q.trim())}`);
      setSearchOpen(false);
      setMobileOpen(false);
      setQ("");
    }
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-neutral-200">
      {/* Promo bar */}
      <div className="text-white text-center text-[11px] md:text-xs tracking-wide py-2 px-4" style={{ backgroundColor: "var(--brand)" }}>
        {tr(settings.promoText)}
      </div>

      {/* Main bar */}
      <div className="max-w-[1400px] mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Left: mobile menu + search */}
          <div className="flex items-center gap-4 flex-1">
            <button className="lg:hidden" onClick={() => setMobileOpen(true)} aria-label="menu">
              <Menu size={22} />
            </button>
            <button className="hidden lg:flex items-center gap-2 text-neutral-700 hover:text-[color:var(--brand)] transition-colors" onClick={() => setSearchOpen((s) => !s)}>
              <Search size={19} />
              <span className="text-xs tracking-widest uppercase font-light">{t("search")}</span>
            </button>
          </div>

          {/* Center: logo */}
          <Link to="/" className="flex flex-col items-center leading-none select-none">
            {settings.logoImage ? (
              <img src={settings.logoImage} alt={settings.brandName} className="h-10 md:h-12 object-contain" />
            ) : (
              <>
                <span className="brand-font text-2xl md:text-4xl tracking-[0.25em] text-neutral-900">{settings.brandName}</span>
                <span className="text-[9px] md:text-[10px] tracking-[0.4em] text-neutral-500 uppercase mt-1">{settings.brandSub}</span>
              </>
            )}
          </Link>

          {/* Right: lang, wishlist, account */}
          <div className="flex items-center gap-4 md:gap-5 flex-1 justify-end">
            <div className="flex items-center text-xs tracking-widest font-light">
              <button onClick={() => setLang("tr")} className={lang === "tr" ? "text-[color:var(--brand)] font-medium" : "text-neutral-400 hover:text-neutral-700"}>TR</button>
              <span className="mx-1 text-neutral-300">|</span>
              <button onClick={() => setLang("en")} className={lang === "en" ? "text-[color:var(--brand)] font-medium" : "text-neutral-400 hover:text-neutral-700"}>EN</button>
            </div>
            <Link to="/compare" className="relative text-neutral-700 hover:text-[color:var(--brand)] transition-colors" title={t("compare")}>
              <BarChart2 size={20} />
              {compare.length > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] text-white w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--brand)" }}>{compare.length}</span>
              )}
            </Link>
            <Link to="/wishlist" className="relative text-neutral-700 hover:text-[color:var(--brand)] transition-colors">
              <Heart size={20} />
              {wishlist.length > 0 && (
                <span className="absolute -top-2 -right-2 text-[10px] text-white w-4 h-4 rounded-full flex items-center justify-center" style={{ backgroundColor: "var(--brand)" }}>{wishlist.length}</span>
              )}
            </Link>
            <Link to={user ? "/account" : "/auth"} className="text-neutral-700 hover:text-[color:var(--brand)] transition-colors flex items-center gap-1.5">
              <User size={20} />
              <span className="hidden md:inline text-xs tracking-widest uppercase font-light">{user ? user.name.split(" ")[0] : t("login")}</span>
            </Link>
          </div>
        </div>

        {/* Search bar dropdown */}
        {searchOpen && (
          <form onSubmit={doSearch} className="pb-4 -mt-2">
            <div className="flex items-center border-b border-neutral-300 py-2">
              <Search size={18} className="text-neutral-400" />
              <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchProducts")} className="flex-1 bg-transparent outline-none px-3 text-sm" />
              <button type="button" onClick={() => setSearchOpen(false)}><X size={18} className="text-neutral-400" /></button>
            </div>
          </form>
        )}

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center justify-center gap-9 h-12 border-t border-neutral-100">
          <Link to="/" className="nav-link">{t("home")}</Link>
          {categories.map((c) => (
            <div key={c.id} className="relative h-full flex items-center" onMouseEnter={() => setOpenCat(c.id)} onMouseLeave={() => setOpenCat(null)}>
              <Link to={`/shop/${c.slug}`} className="nav-link flex items-center gap-1">
                {tr(c.name)}
                {c.subcategories?.length > 0 && <ChevronDown size={13} />}
              </Link>
              {c.subcategories?.length > 0 && openCat === c.id && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 bg-white border border-neutral-100 shadow-xl py-3 min-w-[210px] z-50">
                  {c.subcategories.map((s) => (
                    <Link key={s.id} to={`/shop/${c.slug}/${s.slug}`} className="block px-5 py-2 text-xs tracking-wide text-neutral-600 hover:text-[color:var(--brand)] hover:bg-neutral-50 transition-colors">
                      {tr(s.name)}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <Link to="/about" className="nav-link">{t("about")}</Link>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div className="fixed inset-0 z-[60] lg:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm bg-white overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b">
              <span className="brand-font text-xl tracking-[0.2em]">{settings.brandName}</span>
              <button onClick={() => setMobileOpen(false)}><X size={22} /></button>
            </div>
            <form onSubmit={doSearch} className="p-4 border-b">
              <div className="flex items-center border border-neutral-200 rounded px-3 py-2">
                <Search size={16} className="text-neutral-400" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("searchProducts")} className="flex-1 bg-transparent outline-none px-2 text-sm" />
              </div>
            </form>
            <div className="p-2">
              <Link to="/" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm tracking-wide border-b border-neutral-50">{t("home")}</Link>
              {categories.map((c) => (
                <div key={c.id} className="border-b border-neutral-50">
                  <Link to={`/shop/${c.slug}`} onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm tracking-wide font-medium">{tr(c.name)}</Link>
                  <div className="pb-2">
                    {c.subcategories?.map((s) => (
                      <Link key={s.id} to={`/shop/${c.slug}/${s.slug}`} onClick={() => setMobileOpen(false)} className="block px-8 py-2 text-xs text-neutral-500">{tr(s.name)}</Link>
                    ))}
                  </div>
                </div>
              ))}
              <Link to="/about" onClick={() => setMobileOpen(false)} className="block px-4 py-3 text-sm tracking-wide border-b border-neutral-50">{t("about")}</Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}
